import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useGetProductSupplierByIdQuery,
  useUpdateProductSupplierMutation,
} from '@app/api/Products';
import type { ModelProductSupplier } from '@app/api/Products';
import type { PhoneValidation } from '@app/components/Phone/types';
import { SUPPLIERS_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type {
  AddEditSupplierForm,
  AddEditSupplierProps,
} from '@app/pages/Warehouses/Suppliers/components/AddEditSupplier/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

const { useForm } = Form;

export const useAddEditSupplier = ({ mode }: AddEditSupplierProps) => {
  const [searchParams] = useSearchParams();
  const { activeBranchId: ActiveBranchId, activeBranchCountryId } =
    useUserPrint();
  const { showNotification } = useNotification();
  const [updateSupplier, { isLoading: isUpdateSupplierLoading }] =
    useUpdateProductSupplierMutation();

  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });
  const [form] = useForm<AddEditSupplierForm>();
  const { resetFields, setFieldsValue } = form;

  const {
    userAccessRights: {
      Warehouse: { IsSupplierCreate, IsSupplierEdit, IsSupplierDelete },
    },
  } = useNewUserAccessRights();

  const supplierId = useMemo(() => {
    if (mode !== 'edit') {
      return null;
    }

    const id = searchParams.get('id');

    if (id) {
      const parsedId = parseInt(id, 10);
      if (isNaN(parsedId)) {
        showNotification(
          'Некорректный идентификатор поставщика. Пожалуйста, проверьте URL.',
          'frontWarning'
        );
        return null;
      }
      return parsedId;
    }
    return null;
  }, [searchParams, mode, showNotification]);

  const { data, isFetching, isLoading, isUninitialized } =
    useGetProductSupplierByIdQuery(
      {
        ActiveBranchId,
        supplierId: supplierId || 0,
      },
      { skip: !supplierId || mode !== 'edit' || !IsSupplierEdit }
    );

  const disabledForm = useMemo(() => {
    switch (mode) {
      case 'add':
        return isUpdateSupplierLoading;
      case 'edit':
        return (
          isFetching || isLoading || isUninitialized || isUpdateSupplierLoading
        );
      default:
        return false;
    }
  }, [isLoading, isFetching, isUninitialized, isUpdateSupplierLoading, mode]);

  const isPageForbidden = useMemo(() => {
    switch (mode) {
      case 'add':
        return !IsSupplierCreate;
      case 'edit':
        return !IsSupplierEdit;
      default:
        return false;
    }
  }, [IsSupplierCreate, IsSupplierEdit, mode]);

  const [initialValues, setInitialValues] = useState<AddEditSupplierForm>({
    Name: '',
    Comment: '',
    Phone: {
      Phone: '',
      CountryId: activeBranchCountryId || 1,
    },
  });

  const skipForm = useCallback(() => {
    resetFields();
  }, [resetFields]);

  const initForm = useCallback(() => {
    if (mode === 'add') {
      skipForm();
      return;
    }
    if (data) {
      const { Name, Comment, Phone1CountryId, Phone } = data;

      const initialValues: AddEditSupplierForm = {
        Name: Name || '',
        Comment: Comment || '',
        Phone: {
          Phone: Phone || '',
          CountryId: Phone1CountryId || activeBranchCountryId || 1,
        },
      };
      setFieldsValue(initialValues);
      setInitialValues(initialValues);
    }
  }, [skipForm, setFieldsValue, mode, data, activeBranchCountryId]);

  const goToSuppliers = useCallback(() => {
    navigate(SUPPLIERS_PATH);
  }, [navigate]);

  const openDeleteModal = useCallback(() => {
    setIsDeleteOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    skipForm();
    closeDeleteModal();
    goToSuppliers();
  }, [skipForm, closeDeleteModal, goToSuppliers]);

  const updateSupplierHandler = useCallback(
    async (values: ModelProductSupplier) => {
      try {
        const response = await updateSupplier({
          ActiveBranchId,
          data: values,
        }).unwrap();

        onCancel();

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [updateSupplier, onCancel, ActiveBranchId]
  );

  const onFinish = useCallback(
    async ({
      Name,
      Comment,
      Phone: { Phone, CountryId },
    }: AddEditSupplierForm) => {
      const { errorMessage } = phoneValidation;

      if (errorMessage) {
        form.setFields([
          {
            name: 'Phone',
            errors: [errorMessage],
          },
        ]);
        return;
      }

      try {
        await updateSupplierHandler({
          Id: supplierId || 0,
          Name,
          Phone1CountryId: CountryId,
          Comment: Comment || '',
          IsDeleted: false,
          Phone: Phone,
        });

        showNotification(
          `Поставщик "${Name}" успешно ${
            mode === 'add' ? 'создан' : 'обновлен'
          }.`,
          'success'
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      showNotification,
      supplierId,
      mode,
      updateSupplierHandler,
      phoneValidation,
      form,
    ]
  );

  const onDelete = useCallback(async () => {
    if (!data) {
      showNotification('Поставщик не найден.', 'frontWarning');
      return;
    }

    try {
      const response = await updateSupplierHandler({
        ...data,
        IsDeleted: true,
      });

      const { Name } = response;

      showNotification(`Поставщик "${Name}" успешно удален.`, 'success');
      setIsDeleteOpen(false);
      skipForm();
      goToSuppliers();
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [data, updateSupplierHandler, showNotification, skipForm, goToSuppliers]);

  return {
    form,
    initForm,
    onFinish,
    openDeleteModal,
    closeDeleteModal,
    onDelete,
    isDeleteOpen,
    onCancel,
    disabledForm,
    skipForm,
    setPhoneValidation,
    initialValues,
    IsSupplierDelete,
    isPageForbidden,
  };
};
